import { defineAsyncComponent } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { GlLabel, GlTooltipDirective, GlIcon, GlLoadingIcon } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { sortBy, uniqueId } from 'lodash-es';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { titleInLinkSafeHtmlConfig } from '~/lib/dompurify';
import boardCardInner from 'ee_else_ce/boards/mixins/board_card_inner';
import { isScopedLabel, convertObjectPropsToCamelCase } from '~/lib/utils/common_utils';
import { updateHistory, queryToObject } from '~/lib/utils/url_utility';
import { sprintf, __ } from '~/locale';
import isShowingLabelsQuery from '~/graphql_shared/client/is_showing_labels.query.graphql';
import UserAvatarLink from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/user_avatar/user_avatar_link.vue?vue3';
import WorkItemTypeIcon from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/work_item_type_icon.vue?vue3';
import IssueMilestone from '/builds/gitlab/gitlab-ee/app/assets/javascripts/issuable/components/issue_milestone.vue?vue3';
import { WORK_ITEM_TYPE_NAME_EPIC } from '~/work_items/constants';
import WorkItemRelationshipIcons from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/shared/work_item_relationship_icons.vue?vue3';
import { ListType } from '../constants';
import IssueDueDate from '/builds/gitlab/gitlab-ee/app/assets/javascripts/boards/components/issue_due_date.vue?vue3';
import IssueTimeEstimate from '/builds/gitlab/gitlab-ee/app/assets/javascripts/boards/components/issue_time_estimate.vue?vue3';
export default {
    name: 'BoardCardInner',
    components: {
        GlLabel,
        GlLoadingIcon,
        GlIcon,
        UserAvatarLink,
        IssueDueDate,
        IssueTimeEstimate,
        WorkItemRelationshipIcons,
        IssueWeight: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/issues/components/issue_weight.vue?vue3')),
        IssueIteration: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/boards/components/issue_iteration.vue?vue3')),
        WorkItemTypeIcon,
        IssueMilestone,
        IssueHealthStatus: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/issues/components/issue_health_status.vue?vue3')),
        EpicCountables: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/vue_shared/components/epic_countables/epic_countables.vue?vue3')),
        WorkItemStatusBadge: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/shared/work_item_status_badge.vue?vue3')),
        BoardCardSessionBadge: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/boards/components/board_card_session_badge.vue?vue3'))
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        boardCardInner
    ],
    inject: [
        'allowSubEpics',
        'rootPath',
        'scopedLabelsAvailable',
        'isEpicBoard',
        'isGroupBoard',
        'disabled'
    ],
    props: {
        item: {
            type: Object,
            required: true
        },
        list: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        updateFilters: {
            type: Boolean,
            required: false,
            default: false
        },
        showWorkItemTypeIcon: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'set-filters',
        'view-all-sessions'
    ],
    data () {
        return {
            limitBeforeCounter: 2,
            maxRender: 3,
            maxCounter: 99
        };
    },
    apollo: {
        // eslint-disable-next-line @gitlab/vue-no-undef-apollo-properties
        isShowingLabels: {
            query: isShowingLabelsQuery,
            update: (data)=>data.isShowingLabels
        }
    },
    computed: {
        isLoading () {
            return this.item.isLoading || this.item.iid === '-1';
        },
        cappedAssignees () {
            // e.g. maxRender is 4,
            // Render up to all 4 assignees if there are only 4 assigness
            // Otherwise render up to the limitBeforeCounter
            if (this.item.assignees.length <= this.maxRender) {
                return this.item.assignees.slice(0, this.maxRender);
            }
            return this.item.assignees.slice(0, this.limitBeforeCounter);
        },
        numberOverLimit () {
            return this.item.assignees.length - this.limitBeforeCounter;
        },
        assigneeCounterTooltip () {
            const { numberOverLimit, maxCounter } = this;
            const count = numberOverLimit > maxCounter ? maxCounter : numberOverLimit;
            return sprintf(__('%{count} more assignees'), {
                count
            });
        },
        assigneeCounterLabel () {
            if (this.numberOverLimit > this.maxCounter) {
                return `${this.maxCounter}+`;
            }
            return `+${this.numberOverLimit}`;
        },
        shouldRenderCounter () {
            if (this.item.assignees.length <= this.maxRender) {
                return false;
            }
            return this.item.assignees.length > this.numberOverLimit;
        },
        itemPrefix () {
            return this.isEpicBoard ? '&' : '#';
        },
        itemId () {
            if (this.item.iid) {
                return `${this.itemPrefix}${this.item.iid}`;
            }
            return false;
        },
        hasChildren () {
            return this.totalIssuesCount + this.totalEpicsCount > 0;
        },
        shouldRenderEpicCountables () {
            return this.isEpicBoard && this.hasChildren;
        },
        showLabelFooter () {
            return this.isShowingLabels && this.item.labels.filter(this.isNonListLabel).length > 0;
        },
        itemReferencePath () {
            const { referencePath } = this.item;
            return referencePath.split(this.itemPrefix)[0];
        },
        directNamespaceReference () {
            return this.itemReferencePath.split('/').slice(-1)[0];
        },
        orderedLabels () {
            return sortBy(this.item.labels.filter(this.isNonListLabel), 'title');
        },
        descendantCounts () {
            return this.item.descendantCounts;
        },
        descendantWeightSum () {
            return this.item.descendantWeightSum;
        },
        totalEpicsCount () {
            return this.descendantCounts.openedEpics + this.descendantCounts.closedEpics;
        },
        totalIssuesCount () {
            return this.descendantCounts.openedIssues + this.descendantCounts.closedIssues;
        },
        showReferencePath () {
            return this.isGroupBoard && this.itemReferencePath;
        },
        avatarSize () {
            return {
                default: 16,
                lg: 24
            };
        },
        showBoardCardNumber () {
            return this.item.referencePath && !this.isLoading;
        },
        hasActions () {
            return !this.disabled && this.list.listType !== ListType.closed;
        },
        workItemType () {
            return this.isEpicBoard ? WORK_ITEM_TYPE_NAME_EPIC : this.item.type;
        },
        workItemTypeIconName () {
            return this.item.workItemType?.iconName;
        },
        workItemTypeName () {
            return this.item.workItemType?.name;
        },
        workItemFullPath () {
            return this.item.namespace?.fullPath || this.item.referencePath?.split(this.itemPrefix)[0];
        },
        blockingCount () {
            return this.item?.blockingCount || 0;
        },
        blockedByCount () {
            return this.item?.blockedByCount || 0;
        },
        hasBlockingRelationships () {
            return this.blockingCount > 0 || this.blockedByCount > 0;
        },
        targetId () {
            return uniqueId(`${this.item.iid}`);
        },
        isNotListStatus () {
            return this.item?.status?.id !== this.list?.status?.id;
        },
        showStatus () {
            return this.hasStatus && this.isNotListStatus;
        },
        hasStatus () {
            return Boolean(this.item.status);
        }
    },
    titleInLinkSafeHtmlConfig,
    methods: {
        assigneeUrl (assignee) {
            if (!assignee) return '';
            return `${this.rootPath}${assignee.username}`;
        },
        avatarUrlTitle (assignee) {
            return sprintf(__(`Avatar for %{assigneeName}`), {
                assigneeName: assignee.name
            });
        },
        avatarUrl (assignee) {
            return assignee.avatarUrl || assignee.avatar || gon.default_avatar_url;
        },
        isNonListLabel (label) {
            return label.id && !((this.list.type || this.list.listType) === ListType.label && this.list.title === label.title);
        },
        filterByLabel (label) {
            if (!this.updateFilters) return;
            const filterPath = window.location.search ? `${window.location.search}&` : '?';
            const filter = `label_name[]=${encodeURIComponent(label.title)}`;
            if (!filterPath.includes(filter)) {
                updateHistory({
                    url: `${filterPath}${filter}`
                });
                const rawFilterParams = queryToObject(window.location.search, {
                    gatherArrays: true
                });
                const filters = convertObjectPropsToCamelCase(rawFilterParams, {});
                this.$emit('set-filters', filters);
            }
        },
        showScopedLabel (label) {
            return this.scopedLabelsAvailable && isScopedLabel(label);
        },
        createIssueNumberAriaLabel () {
            const itemType = this.item.type;
            const formattedItemType = `${itemType.charAt(0)}${itemType.slice(1).toLowerCase()}`;
            return sprintf(__(`%{itemType} number %{itemIid}`), {
                itemType: formattedItemType,
                itemIid: this.item.iid
            });
        }
    }
};
